// Purchasing › Suppliers › New supplier and Edit (UC-PURCHASING-001, -002; /purchasing/suppliers/new and …/{id}/edit — the
// prototype draws no supplier form, so it is designed from the use cases): the name, types, contact and address; ordering — Email
// PDF with its order email and up to four copies, or Print (Hoyalog is on the roadmap and not offered); lead time and terms; VAT;
// the default discount; the Hoyalog data hooks, which send nothing. An edit sends only what changed under the version it read;
// a stale version says so and keeps what was typed. A refusal shows under its field in words, never by its API name.
import {
  AVAILABLE_ORDER_CHANNELS, HOYALOG_MANUFACTURER_CODES, ORDER_CHANNEL_LABELS, PURCHASING_LIMITS, SUPPLIER_TYPES,
  type HoyalogManufacturerCode, type SupplierType, type SupplierView,
} from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { newIdempotencyKey } from "../api/client";
import { suppliers, type SupplierBody } from "../api/endpoints.purchasing";
import { carriedOut, describeError, isApiError } from "../api/problem";
import { bpFromPercentText, percentTextFromBp } from "../lib/money-input";
import { can, useSessionView } from "../session/store";
import { useOneStore } from "../session/one-store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Checkbox, ErrorAlert, Field, Loading, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { Icon } from "../ui/icons";
import { SUPPLIER_TYPE_LABEL, activeBranchName } from "./CatalogueShared";
import { NoAccessPanel } from "./common";
import { useLeaveGuard } from "./PatientsShared";

type Channel = (typeof AVAILABLE_ORDER_CHANNELS)[number];
interface Draft {
  name: string; types: SupplierType[]; contactName: string; phone: string; email: string;
  line1: string; line2: string; city: string; province: string; postalCode: string; country: string;
  orderChannel: Channel; orderEmail: string; cc: string[]; leadTimeDays: string; paymentTermsDays: string;
  isVatRegistered: boolean; vatNumber: string; discount: string;
  hoyalogEnabled: boolean; hoyalogCode: HoyalogManufacturerCode; hoyalogCountry: string; lensCatalogueCode: string; notes: string;
}
const EMPTY: Draft = {
  name: "", types: [], contactName: "", phone: "", email: "", line1: "", line2: "", city: "", province: "", postalCode: "", country: "ZA",
  orderChannel: "EmailPdf", orderEmail: "", cc: [], leadTimeDays: "", paymentTermsDays: "", isVatRegistered: true, vatNumber: "", discount: "0",
  hoyalogEnabled: false, hoyalogCode: "none", hoyalogCountry: "SA", lensCatalogueCode: "", notes: "",
};
const num = (n: number | null): string => (n === null ? "" : String(n));
function draftOf(v: SupplierView): Draft {
  return {
    name: v.name, types: [...v.types], contactName: v.contactName ?? "", phone: v.phone ?? "", email: v.email ?? "",
    line1: v.address.line1 ?? "", line2: v.address.line2 ?? "", city: v.address.city ?? "", province: v.address.province ?? "", postalCode: v.address.postalCode ?? "", country: v.address.country,
    orderChannel: v.orderChannel === "Print" ? "Print" : "EmailPdf", orderEmail: v.orderEmail ?? "", cc: [...v.orderEmailCc], leadTimeDays: num(v.leadTimeDays), paymentTermsDays: num(v.paymentTermsDays),
    isVatRegistered: v.isVatRegistered, vatNumber: v.vatNumber ?? "", discount: percentTextFromBp(v.defaultDiscountBp),
    hoyalogEnabled: v.hoyalog.enabled, hoyalogCode: v.hoyalog.manufacturerCode, hoyalogCountry: v.hoyalog.countryCode, lensCatalogueCode: v.lensCatalogueCode ?? "", notes: v.notes ?? "",
  };
}
const orNull = (s: string): string | null => (s.trim() === "" ? null : s.trim());
const intOrNull = (s: string): number | null | "bad" => (s.trim() === "" ? null : /^\d+$/.test(s.trim()) ? Number(s.trim()) : "bad");
const ADDRESS: readonly (keyof Draft & ("line1" | "line2" | "city" | "province" | "postalCode"))[] = ["line1", "line2", "city", "province", "postalCode"];

/** The words under a field the server refused: its own message, but in words for the generic number and address codes. */
function wordsFor(path: string, code: string, message: string): string {
  if (path === "leadTimeDays") return "0 to 60 whole working days";
  if (path === "paymentTermsDays") return "0 to 365 whole days";
  if (path === "defaultDiscountBp") return "0 to 100%, to two decimals";
  if ((path === "email" || path === "orderEmail" || /^orderEmailCc\.\d+$/.test(path)) && (code === "invalid_format" || code === "invalid_string")) return "Not an email address";
  if (path === "address.country" || path === "hoyalog.countryCode") return "Two capital letters, such as ZA";
  if (path === "types" && (code === "too_short" || code === "required")) return "Choose at least one type";
  if (path === "lensCatalogueCode" && code !== "too_long") return "Lower-case letters and digits only";
  return message;
}
const PLACED = new Set(["name", "types", "contactName", "phone", "email", "address.line1", "address.line2", "address.city", "address.province", "address.postalCode", "address.country",
  "orderChannel", "orderEmail", "orderEmailCc", "leadTimeDays", "paymentTermsDays", "vatNumber", "defaultDiscountBp", "hoyalog.manufacturerCode", "hoyalog.countryCode", "hoyalog.enabled", "lensCatalogueCode", "notes"]);

export function SupplierForm() {
  const s = useSessionView();
  const { id } = useParams();
  if (!can(s, "purchasing.supplier.write")) {
    return <section className="screen" id="s-supplier-form"><div className="content"><NoAccessPanel page={id ? "Edit supplier" : "New supplier"} permission="purchasing.supplier.write" /></div></section>;
  }
  return id ? <EditSupplier id={id} /> : <SupplierFormBody base={null} />;
}

function EditSupplier({ id }: { id: string }) {
  const q = useQuery({ queryKey: ["supplier", id], queryFn: () => suppliers.get(id) });
  if (q.isPending) return <section className="screen" id="s-supplier-form"><div className="content"><Loading what="the supplier" /></div></section>;
  if (q.isError) return <section className="screen" id="s-supplier-form"><div className="content"><ErrorAlert error={q.error} /></div></section>;
  return <SupplierFormBody base={{ view: q.data.data, etag: q.data.etag }} />;
}

function SupplierFormBody({ base: live }: { base: { view: SupplierView; etag: string | null } | null }) {
  // A practice of one store: the supplier is the store's, its account number the store's (UC-PURCHASING-001 A6; C13-2).
  const oneStore = useOneStore();
  const store = activeBranchName(useSessionView());
  const qc = useQueryClient();
  const navigate = useNavigate();
  const block = useWriteBlock();
  const online = block === null;
  const [base, setBase] = useState(live);
  const [d, setD] = useState<Draft>(() => (base ? draftOf(base.view) : { ...EMPTY }));
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  useLeaveGuard(dirty && !busy);
  const [idem] = useState(() => newIdempotencyKey());
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => { setD((x) => ({ ...x, [k]: v })); setDirty(true); };
  const serverErr = (path: string): string | undefined => {
    if (!isApiError(error) || error.code !== "VALIDATION_FAILED") return undefined;
    const f = error.errors.find((e) => e.path === path || (path === "orderEmailCc" && e.path.startsWith("orderEmailCc")));
    return f ? wordsFor(f.path, f.code, f.message) : undefined;
  };
  const err = (path: string): string | undefined => local[path] ?? serverErr(path);
  const unplaced = isApiError(error) && error.code === "VALIDATION_FAILED" ? error.errors.filter((f) => !PLACED.has(f.path) && !f.path.startsWith("orderEmailCc")) : [];

  const body = (): SupplierBody | null => {
    const issues: Record<string, string> = {};
    const lead = intOrNull(d.leadTimeDays), terms = intOrNull(d.paymentTermsDays), discount = bpFromPercentText(d.discount.trim() === "" ? "0" : d.discount);
    if (lead === "bad") issues.leadTimeDays = "0 to 60 whole working days";
    if (terms === "bad") issues.paymentTermsDays = "0 to 365 whole days";
    if (discount === null) issues.defaultDiscountBp = "0 to 100%, to two decimals";
    setLocal(issues);
    if (Object.keys(issues).length > 0) return null;
    const cc = d.cc.map((x) => x.trim()).filter(Boolean);
    const address: Record<string, string | null> = { country: d.country.trim().toUpperCase() || "ZA" };
    for (const k of ADDRESS) address[k] = orNull(d[k]);
    const full: SupplierBody = {
      name: d.name.trim(), types: d.types, contactName: orNull(d.contactName), phone: orNull(d.phone), email: orNull(d.email), address,
      orderChannel: d.orderChannel, orderEmail: orNull(d.orderEmail), orderEmailCc: cc, leadTimeDays: lead, paymentTermsDays: terms,
      isVatRegistered: d.isVatRegistered, vatNumber: orNull(d.vatNumber), defaultDiscountBp: discount,
      hoyalog: { enabled: d.hoyalogEnabled, manufacturerCode: d.hoyalogCode, countryCode: d.hoyalogCountry.trim().toUpperCase() || "SA" },
      lensCatalogueCode: orNull(d.lensCatalogueCode), notes: orNull(d.notes),
    };
    if (!base) {
      // A new supplier sends what it has: an empty optional field is left out, not sent as nothing.
      const created = Object.fromEntries(Object.entries(full).filter(([, v]) => v !== null));
      created.address = Object.fromEntries(Object.entries(address).filter(([, v]) => v !== null));
      return created;
    }
    // An edit sends only what changed; the address and the Hoyalog data key by key (UC-PURCHASING-002).
    const was = draftOf(base.view);
    const out: SupplierBody = {};
    const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
    const before: SupplierBody = {
      name: was.name.trim(), types: was.types, contactName: orNull(was.contactName), phone: orNull(was.phone), email: orNull(was.email),
      orderChannel: was.orderChannel, orderEmail: orNull(was.orderEmail), orderEmailCc: was.cc, leadTimeDays: intOrNull(was.leadTimeDays), paymentTermsDays: intOrNull(was.paymentTermsDays),
      isVatRegistered: was.isVatRegistered, vatNumber: orNull(was.vatNumber), defaultDiscountBp: bpFromPercentText(was.discount || "0"), lensCatalogueCode: orNull(was.lensCatalogueCode), notes: orNull(was.notes),
    };
    for (const [k, v] of Object.entries(before)) if (!same(full[k], v)) out[k] = full[k];
    const addr: Record<string, string | null> = {};
    for (const k of ADDRESS) if (orNull(d[k]) !== orNull(was[k])) addr[k] = orNull(d[k]);
    if ((d.country.trim().toUpperCase() || "ZA") !== was.country) addr.country = d.country.trim().toUpperCase() || "ZA";
    if (Object.keys(addr).length > 0) out.address = addr;
    const hl: Record<string, unknown> = {};
    if (d.hoyalogEnabled !== was.hoyalogEnabled) hl.enabled = d.hoyalogEnabled;
    if (d.hoyalogCode !== was.hoyalogCode) hl.manufacturerCode = d.hoyalogCode;
    if ((d.hoyalogCountry.trim().toUpperCase() || "SA") !== was.hoyalogCountry) hl.countryCode = d.hoyalogCountry.trim().toUpperCase() || "SA";
    if (Object.keys(hl).length > 0) out.hoyalog = hl;
    return out;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const b = body();
    if (!b) return;
    setError(null);
    if (base && Object.keys(b).length === 0) { navigate(`/purchasing/suppliers/${base.view.id}`); return; }
    if (base && !base.etag) { setError(new Error("The supplier was read without a version; reload and try again.")); return; }
    setBusy(true);
    try {
      const { data } = base ? await suppliers.patch(base.view.id, b, base.etag!) : await suppliers.create(b, idem);
      setDirty(false);
      void qc.invalidateQueries({ queryKey: ["suppliers"] });
      void qc.invalidateQueries({ queryKey: ["supplier", data.id] });
      navigate(`/purchasing/suppliers/${data.id}`, { state: { notice: base ? `${data.name} saved.` : `${data.name} added.` } });
    } catch (err) {
      // Add supplier pressed again after a lost answer, under the form's one key, answered as carried out: added once, its answer
      // not kept — the suppliers, read again, list it (UC-PURCHASING-001 A4 step 2; the verification of round 3's fixes).
      if (!base && carriedOut(err)) {
        setDirty(false);
        void qc.invalidateQueries({ queryKey: ["suppliers"] });
        navigate("/purchasing/suppliers", { state: { notice: `${d.name.trim()} added: its answer did not arrive, so it is listed here.` } });
        return;
      }
      setError(err);
    }
    finally { setBusy(false); }
  };
  /** UC-PURCHASING-002 E3: read the supplier as it is now, keeping what was typed over the fields it had. */
  const reload = async () => {
    if (!base) return;
    setBusy(true);
    try {
      const fresh = await suppliers.get(base.view.id);
      const was = draftOf(base.view), now = draftOf(fresh.data);
      const merged = { ...now } as Record<keyof Draft, unknown>;
      for (const k of Object.keys(d) as (keyof Draft)[]) if (JSON.stringify(d[k]) !== JSON.stringify(was[k])) merged[k] = d[k];
      setD(merged as unknown as Draft);
      setBase({ view: fresh.data, etag: fresh.etag });
      setError(null);
    } catch (e) { setError(e); }
    finally { setBusy(false); }
  };

  const tin = (k: keyof Draft, label: ReactNode, path: string, o: { required?: boolean; mono?: boolean; max?: number; type?: string; help?: ReactNode; className?: string; inputMode?: "numeric" | "decimal" | "tel" | "email"; placeholder?: string } = {}) => (
    <div className={o.className}>
      <Field label={label} required={o.required} htmlFor={`sf-${path}`} error={err(path)} help={o.help}>
        <TextInput id={`sf-${path}`} value={String(d[k] ?? "")} mono={o.mono} maxLength={o.max} type={o.type} inputMode={o.inputMode} placeholder={o.placeholder} autoComplete="off" error={!!err(path)} onChange={(e) => set(k, e.target.value as never)} />
      </Field>
    </div>
  );
  const heading = base ? `Edit ${base.view.name}` : "New supplier";
  const cancelTo = base ? `/purchasing/suppliers/${base.view.id}` : "/purchasing/suppliers";
  const stale = isApiError(error) && error.code === "VERSION_CONFLICT";
  return (
    <section className="screen" id="s-supplier-form">
      <div className="content">
        <form className="stack" style={{ gap: 16 }} noValidate onSubmit={(e) => void submit(e)}>
          <div className="page-head">
            <div><h1>{heading}</h1><div className="sub">{oneStore && store ? `${store}'s account number is kept with it` : "One supplier for every branch · each branch keeps its own account number with it"}</div></div>
            <div className="actions">
              <Link className="btn" to={cancelTo}>Cancel</Link>
              <button className="btn primary" type="submit" disabled={busy || !online} title={block ? WRITE_BLOCK_TITLE[block] : undefined}>{busy ? "Saving…" : base ? "Save changes" : "Add supplier"}</button>
            </div>
          </div>
          {stale && <Alert tone="warn" role="alert" actions={<button className="btn sm" type="button" disabled={busy} onClick={() => void reload()}>Reload</button>}><b>This supplier was changed by someone else — reload to see the changes.</b> What you typed is kept.</Alert>}
          {isApiError(error) && error.code === "NOT_FOUND" && <Alert tone="crit" role="alert"><b>This supplier was not found.</b> It may have been deleted.</Alert>}
          {isApiError(error) && !stale && error.code !== "NOT_FOUND" && error.code !== "VALIDATION_FAILED" && <Alert tone="crit" role="alert"><b>Not saved.</b> {describeError(error)}</Alert>}
          {!isApiError(error) && error !== null && <Alert tone="crit" role="alert"><b>Not saved.</b> {describeError(error)}</Alert>}
          {unplaced.length > 0 && <Alert tone="crit" role="alert"><b>Not saved.</b> {unplaced.map((f) => wordsFor(f.path, f.code, f.message)).join(" · ")}</Alert>}
          <div className="split wide">
            <div className="stack" style={{ gap: 16 }}>
              <Panel title="Supplier" meta="fields marked * are required">
                <div className="stack">
                  {tin("name", "Name", "name", { required: true, max: PURCHASING_LIMITS.supplierNameMax })}
                  <Field label="Types" required error={err("types")} help="One or more; Sprint 7 reads them for lab orders and purchase orders">
                    <div className="row c wrap" role="group" aria-label="Types">
                      {SUPPLIER_TYPES.map((t) => <Checkbox key={t} checked={d.types.includes(t)} onChange={(v) => set("types", v ? SUPPLIER_TYPES.filter((x) => x === t || d.types.includes(x)) : d.types.filter((x) => x !== t))}>{SUPPLIER_TYPE_LABEL[t]}</Checkbox>)}
                    </div>
                  </Field>
                  <div className="row">
                    {tin("contactName", "Contact name", "contactName", { max: PURCHASING_LIMITS.contactNameMax, className: "grow" })}
                    {tin("phone", "Phone", "phone", { inputMode: "tel", className: "grow", placeholder: "012 348 1122" })}
                  </div>
                  {tin("email", "Email", "email", { type: "email", inputMode: "email", max: PURCHASING_LIMITS.emailMax, help: "For correspondence; orders go to the order email" })}
                  <div className="row">{tin("line1", "Street", "address.line1", { className: "grow" })}{tin("line2", "Line 2", "address.line2", { className: "grow" })}</div>
                  <div className="row">
                    {tin("city", "City", "address.city", { className: "grow" })}
                    {tin("province", "Province", "address.province", { className: "grow" })}
                    {tin("postalCode", "Postal code", "address.postalCode", { mono: true, className: "w-120" })}
                    {tin("country", "Country", "address.country", { mono: true, max: 2, className: "w-80", help: "ISO code" })}
                  </div>
                  <Field label="Notes" htmlFor="sf-notes" error={err("notes")}><TextArea id="sf-notes" value={d.notes} maxLength={PURCHASING_LIMITS.supplierNotesMax} rows={2} onChange={(e) => set("notes", e.target.value)} /></Field>
                </div>
              </Panel>
              <Panel title="Ordering" meta="used by purchase orders from Sprint 7">
                <div className="stack">
                  <div className="row">
                    <div className="grow">
                      <Field label="Order channel" required htmlFor="sf-orderChannel" error={err("orderChannel")} help="Hoyalog ordering is on the roadmap">
                        <Select id="sf-orderChannel" value={d.orderChannel} onChange={(e) => set("orderChannel", e.target.value as Channel)}>
                          {AVAILABLE_ORDER_CHANNELS.map((c) => <option key={c} value={c}>{ORDER_CHANNEL_LABELS[c]}</option>)}
                        </Select>
                      </Field>
                    </div>
                    {tin("leadTimeDays", "Lead time (working days)", "leadTimeDays", { mono: true, inputMode: "numeric", help: "0 to 60; blank proposes no date" })}
                  </div>
                  {tin("orderEmail", "Order email", "orderEmail", { required: d.orderChannel === "EmailPdf", type: "email", inputMode: "email", max: PURCHASING_LIMITS.emailMax, help: d.orderChannel === "EmailPdf" ? "Purchase orders are emailed here as a PDF" : "Optional when orders are printed" })}
                  <Field label="Copy orders to" error={err("orderEmailCc")} help={`Up to ${PURCHASING_LIMITS.orderEmailCcMax} more addresses`}>
                    <div className="stack" style={{ gap: 8 }}>
                      {d.cc.map((c, i) => (
                        <div key={i} className="row c">
                          <TextInput aria-label={`Copy ${i + 1}`} type="email" value={c} boxClass="grow" onChange={(e) => set("cc", d.cc.map((x, j) => (j === i ? e.target.value : x)))} />
                          <button className="btn sm" type="button" aria-label={`Remove copy ${i + 1}`} onClick={() => set("cc", d.cc.filter((_, j) => j !== i))}>Remove</button>
                        </div>
                      ))}
                      {d.cc.length < PURCHASING_LIMITS.orderEmailCcMax && <div><button className="btn sm" type="button" onClick={() => set("cc", [...d.cc, ""])}><Icon name="plus" />Add an address</button></div>}
                    </div>
                  </Field>
                </div>
              </Panel>
            </div>
            <div className="stack" style={{ gap: 16 }}>
              <Panel title="Terms and VAT">
                <div className="stack">
                  {tin("paymentTermsDays", "Payment terms (days)", "paymentTermsDays", { mono: true, inputMode: "numeric", help: "0 to 365, optional" })}
                  <Checkbox checked={d.isVatRegistered} onChange={(v) => set("isVatRegistered", v)}>VAT registered</Checkbox>
                  {tin("vatNumber", "VAT number", "vatNumber", { required: d.isVatRegistered, mono: true, max: PURCHASING_LIMITS.vatNumberMax })}
                  {tin("discount", "Default discount %", "defaultDiscountBp", { mono: true, inputMode: "decimal", help: "Off list prices; a price list's own discount overrides it" })}
                </div>
              </Panel>
              <Panel title="Hoyalog (roadmap)" meta="data only · nothing is sent">
                <div className="stack">
                  <Field label="Manufacturer code" htmlFor="sf-hoyalogCode" error={err("hoyalog.manufacturerCode")}>
                    <Select id="sf-hoyalogCode" value={d.hoyalogCode} onChange={(e) => set("hoyalogCode", e.target.value as HoyalogManufacturerCode)}>
                      {HOYALOG_MANUFACTURER_CODES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </Select>
                  </Field>
                  <Checkbox checked={d.hoyalogEnabled} onChange={(v) => set("hoyalogEnabled", v)}>Enabled — for Hoya or Seiko only</Checkbox>
                  {tin("hoyalogCountry", "Country code", "hoyalog.countryCode", { mono: true, max: 2 })}
                  <div className="help">Stored for the roadmap's Hoyalog ordering: no order or file is sent to Hoyalog from Vision X.</div>
                  {tin("lensCatalogueCode", "Lens catalogue code", "lensCatalogueCode", { mono: true, max: PURCHASING_LIMITS.lensCatalogueCodeMax, help: "The platform lens catalogue's maker, such as hoya; checked against nothing yet" })}
                </div>
              </Panel>
            </div>
          </div>
        </form>
      </div>
    </section>
  );
}
